<template>
  <div class="dns-container">
    <div class="page-header">
      <div class="header-left">
        <el-button icon="el-icon-arrow-left" @click="$router.back()"></el-button>
        <h2>{{ domain }} {{ $t('domainList.dnsTitle') }}</h2>
      </div>
    </div>

    <el-card class="dns-card">
      <div class="dns-actions">
        <el-button type="primary" size="small" @click="handleAddDnsRecord">{{ $t('domainList.addRecord') }}</el-button>
      </div>

      <el-table 
        :data="tableData" 
        border 
        style="width: 100%; margin-top: 15px;" 
        v-loading="loading"
        :row-class-name="getRowClassName"
        :span-method="spanMethod"
      >
        <el-table-column prop="type" :label="$t('domainList.recordType')" width="120">
          <template slot-scope="scope">
            <!-- 如果是帮助行，只在第一列显示内容 -->
            <template v-if="scope.row.isHelpRow">
              <div class="dns-help-panel-inline">
                <div class="help-content">
                  <!-- 记录类型帮助 -->
                  <div v-if="activeHelpField === 'type'" class="help-section" data-type="type">
                    <h4>{{ $t('domainList.recordType') }}</h4>
                    <div class="help-item" v-for="(desc, type) in $t('domainList.dnsHelp.recordTypes')" :key="type">
                      <strong>{{ type }}:</strong> {{ desc }}
                    </div>
                  </div>
                  
                  <!-- 主机记录帮助 -->
                  <div v-if="activeHelpField === 'host'" class="help-section" data-type="host">
                    <h4>{{ $t('domainList.recordHost') }}</h4>
                    <div class="help-item" v-for="(desc, host) in $t('domainList.dnsHelp.hostRecord')" :key="host">
                      <strong>{{ host }}:</strong> {{ desc }}
                    </div>
                  </div>
                  
                  <!-- 记录值帮助 -->
                  <div v-if="activeHelpField === 'value'" class="help-section" data-type="value">
                    <h4>{{ $t('domainList.recordValue') }}</h4>
                    <div class="help-item">
                      <strong>{{ getCurrentEditingRecordType() }}:</strong> 
                      {{ $t('domainList.dnsHelp.recordValue.' + getCurrentEditingRecordType()) }}
                    </div>
                  </div>
                  
                  <!-- TTL帮助 -->
                  <div v-if="activeHelpField === 'ttl'" class="help-section" data-type="ttl">
                    <h4>{{ $t('domainList.recordTTL') }}</h4>
                    <div class="help-item">
                      {{ $t('domainList.dnsHelp.ttl') }}
                    </div>
                  </div>
                  
                  <!-- 优先级帮助 -->
                  <div v-if="activeHelpField === 'priority'" class="help-section" data-type="priority">
                    <h4>{{ $t('domainList.recordPriority') }}</h4>
                    <div class="help-item">
                      {{ $t('domainList.dnsHelp.priority') }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <template v-else-if="scope.row.isEditing">
              <el-select 
                v-model="records[scope.row.originalIndex].type" 
                size="mini"
                @focus="setActiveHelpField('type', scope.row.originalIndex)"
                @mouseenter="setActiveHelpField('type', scope.row.originalIndex)"
              >
                <el-option label="A" value="A"></el-option>
                <el-option label="AAAA" value="AAAA"></el-option>
                <el-option label="CNAME" value="CNAME"></el-option>
                <el-option label="MX" value="MX"></el-option>
                <el-option label="TXT" value="TXT"></el-option>
                <el-option label="NS" value="NS"></el-option>
              </el-select>
            </template>
            <template v-else>
              <span>{{ scope.row.type }}</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column prop="name" :label="$t('domainList.recordHost')" width="180">
          <template slot-scope="scope">
            <template v-if="scope.row.isEditing">
              <el-input 
                v-model="records[scope.row.originalIndex].name" 
                size="mini"
                @focus="setActiveHelpField('host', scope.row.originalIndex)"
                @mouseenter="setActiveHelpField('host', scope.row.originalIndex)"
              ></el-input>
            </template>
            <template v-else>
              <span>{{ scope.row.name }}</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column prop="value" :label="$t('domainList.recordValue')" min-width="180">
          <template slot-scope="scope">
            <template v-if="scope.row.isEditing">
              <el-input 
                v-model="records[scope.row.originalIndex].value" 
                size="mini"
                @focus="setActiveHelpField('value', scope.row.originalIndex)"
                @mouseenter="setActiveHelpField('value', scope.row.originalIndex)"
              ></el-input>
            </template>
            <template v-else>
              <span class="domain-value">{{ scope.row.value }}</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column prop="priority" :label="$t('domainList.recordPriority')" width="100">
          <template slot-scope="scope">
            <template v-if="scope.row.isEditing && records[scope.row.originalIndex].type === 'MX'">
              <el-input 
                v-model="records[scope.row.originalIndex].priority" 
                size="mini"
                @focus="setActiveHelpField('priority', scope.row.originalIndex)"
                @mouseenter="setActiveHelpField('priority', scope.row.originalIndex)"
              ></el-input>
            </template>
            <template v-else>
              <span>{{ scope.row.type === 'MX' ? scope.row.priority : '-' }}</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column prop="ttl" :label="$t('domainList.recordTTL')" width="100">
          <template slot-scope="scope">
            <template v-if="scope.row.isEditing">
              <el-input 
                v-model="records[scope.row.originalIndex].ttl" 
                size="mini"
                @focus="setActiveHelpField('ttl', scope.row.originalIndex)"
                @mouseenter="setActiveHelpField('ttl', scope.row.originalIndex)"
              ></el-input>
            </template>
            <template v-else>
              <span>{{ scope.row.ttl }}</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column :label="$t('domainList.operation')" width="150">
          <template slot-scope="scope">
            <template v-if="scope.row.isEditing">
              <el-button type="text" size="mini" @click="saveDnsRecord(scope.row)">{{ $t('domainList.save') }}</el-button>
              <el-button type="text" size="mini" @click="cancelEditDnsRecord(scope.row)">{{ $t('domainList.cancel') }}</el-button>
            </template>
            <template v-else>
              <el-button type="text" size="mini" @click="editDnsRecord(scope.row)">{{ $t('domainList.edit') }}</el-button>
              <el-button type="text" size="mini" class="delete-btn" @click="deleteDnsRecord(scope.row)">{{ $t('domainList.delete') }}</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'DomainDns',
  data() {
    return {
      domain: '',
      domainId: '',
      records: [],
      loading: false,
      activeHelpField: null,
      activeHelpIndex: null
    }
  },
  created() {
    this.domainId = this.$route.params.id
    this.domain = this.$route.query.domain
    this.fetchDnsRecords()
  },
  computed: {
    hasEditingRecord() {
      return this.records.some(record => record.isEditing)
    },
    currentEditingRecord() {
      return this.records.find(record => record.isEditing)
    },
    tableData() {
      const result = []
      this.records.forEach((record, index) => {
        // 添加原始记录，并保存原始索引
        result.push({
          ...record,
          originalIndex: index
        })
        
        // 如果记录正在编辑且有活跃的帮助字段，添加帮助行
        if (record.isEditing && this.activeHelpField && this.activeHelpIndex === index) {
          result.push({
            id: `help-${record.id}`,
            isHelpRow: true,
            parentRecord: record,
            originalIndex: index
          })
        }
      })
      return result
    }
  },
  methods: {
    // 获取DNS记录
    fetchDnsRecords() {
      this.loading = true

      this.axios.post('/admin/domain/dnsList', {
        domain: this.domain
      }, {
        token: true
      }).then(res => {
        const { status, msg, data } = res
        
        if (status === 200 && data && data.data) {
          this.records = (data.data.list.list || []).map(item => ({
            id: item.dns_id,
            type: item.record_type,
            name: item.record,
            value: item.value,
            priority: item.mx || 0,
            ttl: item.ttl || 600,
            isEditing: false,
            originalData: null
          }))
        } else {
          this.$message.error(msg || '获取DNS记录失败')
          this.records = []
        }

        this.loading = false
      }).catch(error => {
        console.error('获取DNS记录失败:', error)
        this.$message.error('获取DNS记录失败，请稍后重试')
        this.loading = false
        this.records = []
      })
    },

    // 添加DNS记录
    handleAddDnsRecord() {
      const newRecord = {
        id: Date.now(),
        type: 'A',
        name: '',
        value: '',
        ttl: 600,
        isEditing: true,
        originalData: null,
        isNew: true
      }
      this.records.unshift(newRecord)
    },

    // 编辑DNS记录
    editDnsRecord(record) {
      // 如果传入的是包含originalIndex的记录，需要找到原始记录
      const originalRecord = record.originalIndex !== undefined ? this.records[record.originalIndex] : record
      
      // 关闭其他记录的编辑状态
      this.records.forEach(r => {
        if (r.id !== originalRecord.id && r.isEditing) {
          this.cancelEditDnsRecord(r)
        }
      })
      
      originalRecord.originalData = JSON.parse(JSON.stringify(originalRecord))
      originalRecord.isEditing = true
      
      // 清除帮助面板
      this.clearActiveHelpField()
    },

    // 取消编辑DNS记录
    cancelEditDnsRecord(record) {
      // 如果传入的是包含originalIndex的记录，需要找到原始记录
      const originalRecord = record.originalIndex !== undefined ? this.records[record.originalIndex] : record
      
      if (originalRecord.isNew) {
        const index = this.records.findIndex(item => item.id === originalRecord.id)
        if (index !== -1) {
          this.records.splice(index, 1)
        }
      } else {
        Object.assign(originalRecord, originalRecord.originalData)
        originalRecord.isEditing = false
        originalRecord.originalData = null
      }
      
      // 清除帮助面板
      this.clearActiveHelpField()
    },

    // 保存DNS记录
    saveDnsRecord(record) {
      // 如果传入的是包含originalIndex的记录，需要找到原始记录
      const originalRecord = record.originalIndex !== undefined ? this.records[record.originalIndex] : record
      
      if (!originalRecord.name || !originalRecord.value) {
        this.$message.error('主机记录和记录值不能为空')
        return
      }

      this.loading = true;
      

      const params = {
        domain: this.domain,
        type: originalRecord.type,
        name: originalRecord.name,
        value: originalRecord.value,
        ttl: originalRecord.ttl || 600
      }

      if (originalRecord.type === 'MX') {
        params.priority = originalRecord.priority || 10
      }

      if (!originalRecord.isNew) {
        params.id = originalRecord.id
      }

      const apiUrl = originalRecord.isNew ? '/admin/domain/addDns' : '/admin/domain/editDns'

      this.axios.post(apiUrl, params, {
        token: true
      }).then(res => {
        const { status, msg, data } = res
        console.log('save dns record res', res);
        if (status === 200) {
          if (originalRecord.isNew && data && data.data && data.data.record_id) {
            originalRecord.id = data.data.record_id
            originalRecord.isNew = false
          }

          originalRecord.isEditing = false
          originalRecord.originalData = null
          this.$message.success(msg || this.$t('domainList.saveSuccess'))
          
          // 清除帮助面板
          this.clearActiveHelpField()
        } else {
          this.$message.error(msg || '保存DNS记录失败')
        }

        this.loading = false
      }).catch(error => {
        console.error('保存DNS记录失败:', error)
        this.$message.error('保存DNS记录失败，请稍后重试')
        this.loading = false
      })
    },

    // 删除DNS记录
    deleteDnsRecord(record) {
      // 如果传入的是包含originalIndex的记录，需要找到原始记录
      const originalRecord = record.originalIndex !== undefined ? this.records[record.originalIndex] : record
      
      this.$confirm(this.$t('domainList.deleteRecordConfirm'), this.$t('system.prompt'), {
        confirmButtonText: this.$t('system.confirm'),
        cancelButtonText: this.$t('system.cancel'),
        type: 'warning'
      }).then(() => {
        this.loading = true

        this.axios.post('/admin/domain/deleteDns', {
          domain: this.domain,
          record_id: originalRecord.id
        }, {
          token: true
        }).then(res => {
          const { status, msg } = res
          console.log('delete dns record res', res);
          if (status === 200) {
            const index = this.records.findIndex(item => item.id === originalRecord.id)
            if (index !== -1) {
              this.records.splice(index, 1)
            }
            this.$message.success(msg || this.$t('domainList.deleteRecordSuccess'))
          } else {
            this.$message.error(msg || '删除DNS记录失败')
          }

          this.loading = false
        }).catch(error => {
          console.error('删除DNS记录失败:', error)
          this.$message.error('删除DNS记录失败，请稍后重试')
          this.loading = false
        })
      }).catch(() => { })
    },

    setActiveHelpField(field, index) {
      this.activeHelpField = field
      this.activeHelpIndex = index
    },

    clearActiveHelpField() {
      this.activeHelpField = null
      this.activeHelpIndex = null
    },

    getRowClassName({ row, rowIndex }) {
      if (row.isHelpRow) {
        return 'help-row'
      }
      return ''
    },

    spanMethod({ row, column, rowIndex, columnIndex }) {
      if (row.isHelpRow) {
        if (columnIndex === 0) {
          return {
            rowspan: 1,
            colspan: 6  // 合并所有6列
          }
        } else {
          return {
            rowspan: 0,
            colspan: 0  // 其他列隐藏
          }
        }
      }
    },

    getCurrentEditingRecordType() {
      if (this.activeHelpIndex !== null && this.records[this.activeHelpIndex]) {
        return this.records[this.activeHelpIndex].type;
      }
      const record = this.currentEditingRecord;
      return record ? record.type : 'A';
    }
  }
}
</script>

<style scoped>
.dns-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-left h2 {
  margin: 0;
  font-size: 22px;
  color: #303133;
}

.dns-card {
  margin-bottom: 20px;
}

.dns-actions {
  margin-bottom: 15px;
}

.delete-btn {
  color: #F56C6C;
}

.el-table {
  font-size: 14px;
}

.el-table .cell span {
  display: block;
  padding: 8px 0;
  color: #606266;
}

/* 为域名值添加特殊样式 */
.el-table .cell span.domain-value {
  font-family: Consolas, Menlo, Monaco, monospace;
  color: #409EFF;
}

/* DNS帮助面板样式 */
.help-panel-container {
  margin-top: 10px;
}

.dns-help-panel {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 15px;
  margin: 10px 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 内联帮助面板样式 */
.dns-help-panel-inline {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #d1ecf1;
  border-radius: 8px;
  padding: 20px;
  margin: 0;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
  width: 100%;
  position: relative;
  transition: all 0.3s ease;
}

.dns-help-panel-inline:hover {
  box-shadow: 0 6px 20px rgba(0, 123, 255, 0.2);
  transform: translateY(-1px);
}

/* 帮助行样式 */
.el-table .el-table__row .dns-help-panel-inline {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #d1ecf1;
  border-radius: 8px;
  padding: 20px;
  margin: 0;
}

/* 帮助行跨列样式 */
.el-table .help-row {
  background: transparent !important;
}

.el-table .help-row td {
  border: none !important;
  padding: 15px !important;
  background: transparent !important;
}

.el-table .help-row td:first-child {
  position: relative;
}

.help-content {
  font-size: 14px;
  line-height: 1.6;
  color: #495057;
}

.help-section h4 {
  margin: 0 0 15px 0;
  color: #007bff;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 2px solid #007bff;
  padding-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.help-section h4:before {
  content: "💡";
  font-size: 18px;
}

.help-item {
  margin-bottom: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 6px;
  border-left: 4px solid #007bff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  position: relative;
}

.help-item:hover {
  transform: translateX(2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-left-color: #0056b3;
}

.help-item:last-child {
  margin-bottom: 0;
}

.help-item strong {
  color: #007bff;
  font-weight: 600;
  margin-right: 8px;
  font-size: 14px;
}

.help-item {
  color: #6c757d;
  font-size: 13px;
}

/* 为不同类型的帮助添加不同的颜色主题 */
.help-section[data-type="type"] .help-item {
  border-left-color: #28a745;
}

.help-section[data-type="type"] h4 {
  color: #28a745;
  border-bottom-color: #28a745;
}

.help-section[data-type="type"] h4:before {
  content: "🏷️";
}

.help-section[data-type="host"] .help-item {
  border-left-color: #ffc107;
}

.help-section[data-type="host"] h4 {
  color: #ffc107;
  border-bottom-color: #ffc107;
}

.help-section[data-type="host"] h4:before {
  content: "🏠";
}

.help-section[data-type="value"] .help-item {
  border-left-color: #17a2b8;
}

.help-section[data-type="value"] h4 {
  color: #17a2b8;
  border-bottom-color: #17a2b8;
}

.help-section[data-type="value"] h4:before {
  content: "📝";
}

.help-section[data-type="ttl"] .help-item {
  border-left-color: #6f42c1;
}

.help-section[data-type="ttl"] h4 {
  color: #6f42c1;
  border-bottom-color: #6f42c1;
}

.help-section[data-type="ttl"] h4:before {
  content: "⏰";
}

.help-section[data-type="priority"] .help-item {
  border-left-color: #fd7e14;
}

.help-section[data-type="priority"] h4 {
  color: #fd7e14;
  border-bottom-color: #fd7e14;
}

.help-section[data-type="priority"] h4:before {
  content: "🔢";
}
</style>